import { useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import { envConfig } from "../../config/env";
import { buildWholeWorkflowIframeSrc } from "../../utils/wholeWorkflowIframe";

const PANE_TITLE = "View whole workflow";

/** Shared chrome for the split details + workflow panes. */
export const DETAILS_SPLIT_PANE_SX = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column" as const,
  border: "1px solid #E5E5E5",
  borderRadius: "8px",
  overflow: "hidden",
  bgcolor: "#fff",
};

export interface WholeWorkflowSidePaneProps {
  uri: string;
  onClose: () => void;
  sx?: SxProps<Theme>;
}

export function WholeWorkflowSidePane({
  uri,
  onClose,
  sx,
}: WholeWorkflowSidePaneProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeSrc = buildWholeWorkflowIframeSrc(
    uri,
    envConfig.platformBaseUrl,
  );

  return (
    <Box
      sx={[DETAILS_SPLIT_PANE_SX, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      data-testid="whole-workflow-side-pane"
      aria-label={PANE_TITLE}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: "1px solid #F2F2F2",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 16,
            lineHeight: "24px",
            color: "#172B4D",
          }}
        >
          {PANE_TITLE}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Open in new tab">
            <IconButton
              aria-label="Open in new tab"
              onClick={() =>
                window.open(uri.trim(), "_blank", "noopener,noreferrer")
              }
              sx={DIALOG_CLOSE_BUTTON_SX}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              aria-label="Close whole workflow"
              onClick={onClose}
              sx={DIALOG_CLOSE_BUTTON_SX}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        {!iframeLoaded ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}
        <Box
          component="iframe"
          title={PANE_TITLE}
          src={iframeSrc}
          onLoad={() => setIframeLoaded(true)}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            border: "none",
            bgcolor: "#fff",
          }}
        />
      </Box>
    </Box>
  );
}
